/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * UnityDialogActionRegistry — module-level callback registry.
 *
 * Redux state MUST stay serializable (REDUX_GOVERNANCE.md), so action
 * `onPress` functions can never live in the store. Instead, `showUnityDialog`
 * captures them here, keyed by action `id`, while the store only ever holds
 * the serializable `{ id, label }` pair.
 *
 * Lifecycle: populated by `showUnityDialog` when the dialog opens, consulted
 * and cleared by `triggerUnityDialogAction` whenever any action is activated
 * (click, OK key, or BACK key) — never leaked or left stale between dialogs.
 */

type UnityDialogActionCallback = () => void;

const registry: Map<string, UnityDialogActionCallback> = new Map();

export const UnityDialogActionRegistry = {
  /** Register a callback for the given action id. Overwrites any existing entry for that id. */
  set(id: string, callback: UnityDialogActionCallback): void {
    registry.set(id, callback);
  },

  /** Look up the callback registered for the given action id, if any. */
  get(id: string): UnityDialogActionCallback | undefined {
    return registry.get(id);
  },

  /** Whether a callback is currently registered for the given action id. */
  has(id: string): boolean {
    return registry.has(id);
  },

  /** Remove all registered callbacks. Called after every dismiss to prevent stale references leaking into the next dialog. */
  clear(): void {
    registry.clear();
  }
};
